import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import PiBrowseLayout from "../../components/PiBrowseLayout";
import {
  browseListboxId,
  browseOptionId,
} from "../../components/browseListboxIds";
import useInteractionPicker from "../../hooks/InteractionPicker.hook";
import Interaction from "../../models/interactions/Interaction";
import { InteractionDefintion } from "./InteractionDefinitions";
import InteractionPickerDetails from "./InteractionPickerDetails";
import InteractionPickerRail from "./InteractionPickerRail";
import InteractionPickerResults from "./InteractionPickerResults";
import useInteractionPickerList, {
  DEFAULT_TILE_COLUMNS,
} from "./useInteractionPickerList";

interface InteractionPickerBodyProps {
  onClose: () => void;
  definitions?: InteractionDefintion[];
  onAddInteraction?: (interaction: Interaction) => void;
}

/**
 * Everything inside the dialog. Split from `InteractionPicker` because the
 * dialog element itself renders whether or not it is open, and the add path
 * pulls in training-slice selectors that should not run against a closed
 * picker.
 */
const DIALOG = "interactionpicker";

export default function InteractionPickerBody(props: InteractionPickerBodyProps) {
  const { onClose, definitions, onAddInteraction } = props;
  const { t } = useTranslation();
  const { addNewInteractionFor } = useInteractionPicker();

  // Measured by the results grid and fed back so the arrow keys step by a real
  // row rather than an assumed one.
  const [tileColumns, setTileColumns] = useState(DEFAULT_TILE_COLUMNS);

  const addInteraction = useCallback(
    (definition: InteractionDefintion) => {
      addNewInteractionFor(definition, onAddInteraction);
    },
    // addNewInteractionFor closes over hooks that change every render, so it is
    // intentionally not a dependency — the call itself reads current values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onAddInteraction]
  );

  // Enter goes through the shared hook, which closes for us. The mouse paths
  // have to close themselves — in `finally`, so a failure while creating the
  // interaction cannot leave the dialog stranded open.
  const addAndClose = useCallback(
    (definition: InteractionDefintion) => {
      try {
        addInteraction(definition);
      } finally {
        onClose();
      }
    },
    [addInteraction, onClose]
  );

  const list = useInteractionPickerList({
    definitions,
    open: true,
    onClose,
    onAdd: addInteraction,
    tileColumns,
  });

  const confirmIndex = useCallback(
    (index: number) => {
      const definition = list.flatItems[index];
      if (definition) addAndClose(definition);
    },
    [list.flatItems, addAndClose]
  );

  return (
    <PiBrowseLayout
      searchValue={list.filter}
      onSearchChange={list.setFilter}
      searchPlaceholder={t("interaction_picker_ui.search_placeholder")}
      searchInputRef={list.searchInputRef}
      searchFieldId="interactionpicker-search"
      listboxId={browseListboxId(DIALOG)}
      activeDescendantId={browseOptionId(DIALOG, list.selectedIndex)}
      viewMode={list.viewMode}
      onViewModeChange={list.changeViewMode}
      rail={
        list.showRail ? (
          <InteractionPickerRail
            categories={list.railCategories}
            value={list.categoryScope}
            onChange={list.setCategoryScope}
            getCategoryLabel={list.getCategoryLabel}
          />
        ) : undefined
      }
      results={
        <InteractionPickerResults
          groups={list.groups}
          viewMode={list.viewMode}
          selectedIndex={list.selectedIndex}
          onSelectIndex={list.setSelectedIndex}
          onConfirmIndex={confirmIndex}
          onColumnsChange={setTileColumns}
          listboxId={browseListboxId(DIALOG)}
          optionIdFor={(index) => browseOptionId(DIALOG, index)}
        />
      }
      details={
        <InteractionPickerDetails
          definition={list.highlighted}
          getCategoryLabel={list.getCategoryLabel}
        />
      }
      primaryLabel={t("add_interaction") ?? ""}
      primaryDisabled={!list.highlighted}
      onPrimaryAction={() => list.highlighted && addAndClose(list.highlighted)}
    />
  );
}
